import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, defineMessages } from 'react-intl';

const Success = function Success({ email }) {
    return (
        <FormattedMessage
            id="dbl.authmodal.auth.forgotPassword.success"
            defaultMessage="We've sent an email to {email} with instructions for adding a new password."
            values={{ email }}
        />
    );
};

export const placeholders = defineMessages({
    passwordPlaceholder: {
        id: 'dbl.authmodal.auth.passwordPlaceholder',
        defaultMessage: 'Password',
    },
    emailPlaceholder: {
        id: 'dbl.authmodal.auth.emailPlaceholder',
        defaultMessage: 'Email address',
    },
    currentPasswordPlaceholder: {
        id: 'dbl.authmodal.auth.currentPasswordPlaceholder',
        defaultMessage: 'Current password',
    },
    newPasswordPlaceholder: {
        id: 'dbl.authmodal.auth.newPasswordPlaceholder',
        defaultMessage: 'New password',
    },
    passwordConfPlaceholder: {
        id: 'dbl.authmodal.auth.passwordConfPlaceholder',
        defaultMessage: 'Re-enter new password',
    },
    createPasswordPlaceholder: {
        id: 'dbl.authmodal.auth.completeEmailOnly.passwordPlaceholder',
        defaultMessage: 'Create a password',
    },
});

const privacyPolicyLink = (
    <a href="/about/privacy-policy/" target="_blank">
        <FormattedMessage
            id="dbl.authmodal.auth.acceptPolicyContactDealer.privacyPolicy"
            defaultMessage="Privacy Policy"
        />
    </a>
);
const userAgreementLink = (
    <a href="/about/user-agreement/" target="_blank">
        <FormattedMessage
            id="dbl.authmodal.auth.acceptPolicyContactDealer.userAgreement"
            defaultMessage="User Agreement"
        />
    </a>
);

export default {
    acceptPolicy: (
        <FormattedMessage
            id="dbl.authmodal.auth.acceptPolicy"
            defaultMessage="I have read and accepted 1stdibs' {userAgreementLink} and {privacyPolicyLink}."
            values={{
                privacyPolicyLink,
                userAgreementLink,
            }}
        />
    ),
    acceptPolicyContactDealer: (
        <FormattedMessage
            id="dbl.authmodal.auth.acceptPolicyContactDealer"
            defaultMessage="I accept the 1stdibs' {privacyPolicyLink} and {userAgreementLink}, and understand that sharing contact information to purchase outside of 1stdibs is prohibited."
            values={{
                privacyPolicyLink,
                userAgreementLink,
            }}
        />
    ),
    accountLogin: (
        <FormattedMessage
            id="dbl.authmodal.auth.accountLogin"
            defaultMessage="Already have an account?"
        />
    ),
    accountRegistration: (
        <FormattedMessage
            id="dbl.authmodal.auth.accountRegistration"
            defaultMessage="Don't have an account?"
        />
    ),
    backToLogin: (
        <FormattedMessage id="dbl.authmodal.auth.backToLogin" defaultMessage="< Back to login" />
    ),
    continue: <FormattedMessage id="dbl.authmodal.auth.continue" defaultMessage="Continue" />,
    continueShopping: (
        <FormattedMessage
            id="dbl.authmodal.auth.continueShopping"
            defaultMessage="Continue Shopping"
        />
    ),
    existingAccount: {
        firstLine: (
            <FormattedMessage
                id="dbl.authmodal.auth.existingAccount.l1"
                defaultMessage="An account with this email exists. Please enter your password to log in."
            />
        ),
        secondLine: (
            <FormattedMessage
                id="dbl.authmodal.auth.existingAccount.l2"
                defaultMessage="If you have not yet created one, click the link below."
            />
        ),
    },
    login: <FormattedMessage id="dbl.authmodal.auth.login" defaultMessage="Log in" />,
    loginAccess: (
        <FormattedMessage
            id="dbl.authmodal.auth.loginAccess"
            defaultMessage="Log in to access your account"
        />
    ),
    notRegistered: (
        <FormattedMessage
            id="dbl.authmodal.auth.notRegistered"
            defaultMessage="Not registered yet?"
        />
    ),
    passwordLength: (
        <FormattedMessage
            id="dbl.authmodal.auth.passwordLength"
            defaultMessage="Must be at least 5 characters"
        />
    ),
    proceedCheckout: (
        <FormattedMessage
            id="dbl.authmodal.auth.proceedCheckout"
            defaultMessage="Proceed to checkout."
        />
    ),
    register: <FormattedMessage id="dbl.authmodal.auth.register" defaultMessage="Sign up" />,
    registerPasswordPlaceholder: (
        <FormattedMessage
            id="dbl.authmodal.auth.registerPasswordPlaceholder"
            defaultMessage="Create a password"
        />
    ),
    registrationCompletion: (
        <FormattedMessage
            id="dbl.authmodal.auth.registrationCompletion"
            defaultMessage="Complete registration below to gain full access and get personalized email updates"
        />
    ),
    registrationDescription: (
        <FormattedMessage
            id="dbl.authmodal.auth.registrationDescription"
            defaultMessage="Get access to the best selection of rare and beautiful objects from around the world"
        />
    ),
    resetPassword: (
        <FormattedMessage
            id="dbl.authmodal.auth.resetPassword"
            defaultMessage="Reset or Create Password"
        />
    ),
    resetPasswordSuccess: (
        <FormattedMessage
            id="dbl.authmodal.auth.resetPasswordSuccess"
            defaultMessage="Your password has been reset and you are logged in."
        />
    ),
    socialAccountLinked: (
        <FormattedMessage
            id="dbl.authmodal.auth.socialAccountLinked"
            defaultMessage="Your Accounts Are Now Connected"
        />
    ),
    socialAccountLinkedClose: (
        <FormattedMessage id="dbl.authmodal.auth.socialAccountLinkedClose" defaultMessage="Close" />
    ),
    enterEmailToSignUp: (
        <FormattedMessage
            id="dbl.authmodal.auth.enterEmailToSignUp"
            defaultMessage="Enter Your Email to Sign Up"
        />
    ),
    enterEmailToSignUpDescription: (
        <FormattedMessage
            id="dbl.authmodal.auth.enterEmailToSignUpDescription"
            defaultMessage="Please provide your email address to create a 1stdibs account and connect your Facebook account."
        />
    ),
    updatePassword: (
        <FormattedMessage
            id="dbl.authmodal.auth.updatePassword"
            defaultMessage="Update your password"
        />
    ),
    updatePasswordSuccess: (
        <FormattedMessage
            id="dbl.authmodal.auth.updatePasswordSuccess"
            defaultMessage="Your password has been updated."
        />
    ),
    signUp: <FormattedMessage id="dbl.authmodal.auth.signUp" defaultMessage="Sign Up" />,
    submit: <FormattedMessage id="dbl.authmodal.auth.submit" defaultMessage="Submit" />,
    thankYou: <FormattedMessage id="dbl.authmodal.auth.thankYou" defaultMessage="Thank you." />,
    welcome: (
        <FormattedMessage id="dbl.authmodal.auth.welcome" defaultMessage="Welcome to 1stdibs" />
    ),
    resetInvalidResetPassword: {
        header: (
            <FormattedMessage
                id="dbl.authmodal.auth.resetInvalidResetPassword.header"
                defaultMessage="Password Link Expired"
            />
        ),
        subheader: (
            <FormattedMessage
                id="dbl.authmodal.auth.resetInvalidResetPassword.subheader"
                defaultMessage="Enter your account email address, and we’ll send a new link to add a password."
            />
        ),
    },
    emailTokenLogin: {
        header: (
            <FormattedMessage
                id="dbl.authmodal.auth.emailTokenLogin.header"
                defaultMessage="Log in to continue"
            />
        ),
        subHeader: (
            <FormattedMessage
                id="dbl.authmodal.auth.emailTokenLogin.subheader"
                defaultMessage="Please enter your password to continue"
            />
        ),
        headerOrders: (
            <FormattedMessage
                id="dbl.authmodal.auth.emailTokenLogin.viewOrdersHeader"
                defaultMessage="Log in to View Orders"
            />
        ),
        headerConversations: (
            <FormattedMessage
                id="dbl.authmodal.auth.emailTokenLogin.viewConversationsHeader"
                defaultMessage="Log in to View Conversations"
            />
        ),
        viewInvoiceHeader: (
            <FormattedMessage
                id="dbl.authmodal.auth.emailTokenLogin.viewInvoiceHeader"
                defaultMessage="Log In to View Invoice"
            />
        ),
        callSellerHeader: (
            <FormattedMessage
                id="dbl.authmodal.auth.emailTokenLogin.callSellerHeader"
                defaultMessage="Log In to Call the Seller"
            />
        ),
        requestShippingQuoteHeader: (
            <FormattedMessage
                id="dbl.authmodal.auth.emailTokenLogin.requestShippingQuoteHeader"
                defaultMessage="Log In to Request Shipping Quote"
            />
        ),
    },
    createPassword: {
        header: (
            <FormattedMessage
                id="dbl.authmodal.auth.createPassword.header"
                defaultMessage="Create Password"
            />
        ),
        subHeader: (
            <FormattedMessage
                id="dbl.authmodal.auth.createPassword.subheader"
                defaultMessage="You must have a complete account to continue."
            />
        ),
        note: (
            <FormattedMessage
                id="dbl.authmodal.auth.createPassword.note"
                defaultMessage="Note: your message was not sent to the seller. Please login and try again"
            />
        ),
        viewConversationsHeader: (
            <FormattedMessage
                id="dbl.authmodal.auth.createPassword.viewConversationsHeader"
                defaultMessage="Create Password to View Conversations"
            />
        ),
        viewOrdersdHeader: (
            <FormattedMessage
                id="dbl.authmodal.auth.createPassword.viewOrdersHeader"
                defaultMessage="Create Password to View Orders"
            />
        ),
        acceptOfferHeader: (
            <FormattedMessage
                id="dbl.authmodal.auth.createPassword.acceptOfferHeader"
                defaultMessage="Create Password to Accept Offer"
            />
        ),
        checkoutHeader: (
            <FormattedMessage
                id="dbl.authmodal.auth.createPassword.checkoutHeader"
                defaultMessage="Create Password to Purchase Now"
            />
        ),
        requestShippingQuoteHeader: (
            <FormattedMessage
                id="dbl.authmodal.auth.createPassword.requestShippingQuoteHeader"
                defaultMessage="Create Password to Request Shipping Quote"
            />
        ),
        viewInvoiceHeader: (
            <FormattedMessage
                id="dbl.authmodal.auth.createPassword.viewInvoiceHeader"
                defaultMessage="Create Password to View Invoice"
            />
        ),
        callSellerHeader: (
            <FormattedMessage
                id="dbl.authmodal.auth.createPassword.callSellerHeader"
                defaultMessage="Create Password to Call the Seller"
            />
        ),
        sendMessageHeader: (
            <FormattedMessage
                id="dbl.authmodal.auth.createPassword.sendMessageHeader"
                defaultMessage="Create Password to Send Your Message"
            />
        ),
    },
    forgotPassword: {
        header: (
            <FormattedMessage
                id="dbl.authmodal.auth.forgotPassword.header"
                defaultMessage="Reset or Create Password"
            />
        ),
        subheader: (
            <FormattedMessage
                id="dbl.authmodal.auth.forgotPassword.subheader"
                defaultMessage="Please enter the email address of your account, and we’ll send you a link to add a new password."
            />
        ),
        success: Success,
        support: (
            <FormattedMessage
                id="dbl.authmodal.auth.forgotPassword.support"
                defaultMessage="If you do not receive an email in the next 10 minutes, please check your spam folder. If you still cannot access your account, {userEmail}."
                values={{
                    userEmail: (
                        <a
                            href={`https://support.1stdibs.com/s/article/Changing-Your-Password`}
                            target="_blank"
                            rel="noreferrer noopener"
                        >
                            <FormattedMessage
                                id="dbl.authmodal.auth.forgotPassword.support.contactus"
                                defaultMessage="contact us"
                            />
                        </a>
                    ),
                }}
            />
        ),
    },
    follow: {
        loginWelcome: (
            <FormattedMessage
                id="dbl.authmodal.auth.follow.loginWelcome"
                defaultMessage="Log In to Follow"
            />
        ),
        loginAccess: (
            <FormattedMessage
                id="dbl.authmodal.auth.follow.loginAccess"
                defaultMessage="Log in to your account to follow, and get weekly alerts relevant to this seller, creator, category, or search."
            />
        ),
        registrationWelcome: (
            <FormattedMessage
                id="dbl.authmodal.auth.follow.registrationWelcome"
                defaultMessage="Sign Up to Follow"
            />
        ),
        registrationDescription: (
            <FormattedMessage
                id="dbl.authmodal.auth.follow.registrationDescription"
                defaultMessage="We’ll email you when there are new arrivals relevant to the seller, creator, category or search you follow."
            />
        ),
    },
    completeEmailOnly: {
        header: (
            <FormattedMessage
                id="dbl.authmodal.auth.completeEmailOnly.header"
                defaultMessage="Complete Registration"
            />
        ),
        subheader: (
            <FormattedMessage
                id="dbl.authmodal.auth.completeEmailOnly.subheader"
                defaultMessage="Complete registration below to gain full access and get personalized email updates"
            />
        ),
        submit: (
            <FormattedMessage
                id="dbl.authmodal.auth.completeEmailOnly.submit"
                defaultMessage="Continue"
            />
        ),
    },
    contactDealer: {
        welcome: (
            <FormattedMessage
                id="dbl.authmodal.auth.contactDealer.welcome"
                defaultMessage="Log In to Send Your Message"
            />
        ),
        registrationHeader: (
            <FormattedMessage
                id="dbl.authmodal.auth.contactDealer.registrationHeader"
                defaultMessage="Create an account to send your message and receive a response from the seller."
            />
        ),
        loginAccess: (
            <FormattedMessage
                id="dbl.authmodal.auth.contactDealer.loginAccess"
                defaultMessage="Enter a password to send your message and get access to all the onsite features."
            />
        ),
        registrationCompletion: (
            <FormattedMessage
                id="dbl.authmodal.auth.contactDealer.registrationCompletion"
                defaultMessage="Enter a password to send your message and get access to all 1stdibs features."
            />
        ),
    },
    contactDealerCall: {
        loginAccess: (
            <FormattedMessage
                id="dbl.authmodal.auth.contactDealerCall.loginAccess"
                defaultMessage="Log in to initiate the call with this dealer"
            />
        ),
    },
    saveItem: {
        loginHeader: (
            <FormattedMessage
                id="dbl.authmodal.auth.saveItem.loginHeader"
                defaultMessage="Log In to Save This Item"
            />
        ),
        loginHeaderFolder: (
            <FormattedMessage
                id="dbl.authmodal.auth.saveItem.loginHeaderFolder"
                defaultMessage="Log In to Save This Item to a Folder"
            />
        ),
        registrationHeader: (
            <FormattedMessage
                id="dbl.authmodal.auth.saveItem.registrationHeader"
                defaultMessage="Sign Up to Save This Item"
            />
        ),
        registrationHeaderFolder: (
            <FormattedMessage
                id="dbl.authmodal.auth.saveItem.registrationHeaderFolder"
                defaultMessage="Sign Up to Save This Item to a Folder"
            />
        ),
        subtitle: (
            <FormattedMessage
                id="dbl.authmodal.auth.saveItem.subtitle"
                defaultMessage="We'll alert you when it goes on sale."
            />
        ),
    },
    saveItemPdp: {
        loginHeader: (
            <FormattedMessage
                id="dbl.authmodal.auth.saveItemPdp.loginHeader"
                defaultMessage="Log In to Get Updates on This Item"
            />
        ),
        registrationHeader: (
            <FormattedMessage
                id="dbl.authmodal.auth.saveItemPdp.registrationHeader"
                defaultMessage="Sign Up to Get Updates on This Item"
            />
        ),
        registrationCompletionHeader: (
            <FormattedMessage
                id="dbl.authmodal.auth.saveItemPdp.registrationCompletionHeader"
                defaultMessage="Create a Password"
            />
        ),
    },
    requestShippingQuote: {
        loginHeader: (
            <FormattedMessage
                id="dbl.authmodal.auth.requestShippingQuote.loginHeader"
                defaultMessage="Log In to Request Shipping Quote"
            />
        ),
        registrationHeader: (
            <FormattedMessage
                id="dbl.authmodal.auth.requestShippingQuote.registrationHeader"
                defaultMessage="Sign Up to Request Shipping Quote"
            />
        ),
        registrationCompletionHeader: (
            <FormattedMessage
                id="dbl.authmodal.auth.requestShippingQuote.registrationCompletionHeader"
                defaultMessage="Create a Password"
            />
        ),
    },
    localSaveItem: {
        welcome: (
            <FormattedMessage
                id="dbl.authmodal.auth.localSaveItem.welcome"
                defaultMessage="Save More Beautiful Things"
            />
        ),
        loginAccess: (
            <FormattedMessage
                id="dbl.authmodal.auth.localSaveItem.loginAccess"
                defaultMessage="Please create an account to keep adding favorite items and save them permanently"
            />
        ),
    },
    embeddedCheckout: {
        loginAccess: (
            <FormattedMessage
                id="dbl.authmodal.auth.embeddedCheckout.loginAccess"
                defaultMessage="Log In to check out faster"
            />
        ),
    },
    staleLogin: {
        welcome: (
            <FormattedMessage
                id="dbl.authmodal.auth.staleLogin.welcome"
                defaultMessage="Please Re-Enter Your Password"
            />
        ),
        changeAccount: (
            <FormattedMessage
                id="dbl.authmodal.auth.staleLogin.changeAccount"
                defaultMessage="Not you? {login} with another email address."
                values={{
                    login: (
                        <a data-tn="different-user-login" href="#">
                            <FormattedMessage
                                id="dbl.authmodal.auth.staleLogin.changeAccount.login"
                                defaultMessage="Log in"
                            />
                        </a>
                    ),
                }}
            />
        ),
        dontHaveAccount: (
            <FormattedMessage
                id="dbl.authmodal.auth.staleLogin.dontHaveAccount"
                defaultMessage="Don't have an account?"
            />
        ),
    },
    trade: {
        welcome: (
            <FormattedMessage
                id="dbl.authmodal.auth.trade.welcome"
                defaultMessage="Complete Your Application"
            />
        ),
        loginAccess: (
            <FormattedMessage
                id="dbl.authmodal.auth.trade.loginAccess"
                defaultMessage="Log in to submit your application"
            />
        ),
        registrationAccess: (
            <FormattedMessage
                id="dbl.authmodal.auth.trade.registrationAccess"
                defaultMessage="Create an account to submit your application"
            />
        ),
    },
    errors: {
        conflictRegistration: (
            <FormattedMessage
                id="dbl.authmodal.auth.errors.conflictRegistration"
                defaultMessage="This email address has already been registered. Please enter your password to login."
            />
        ),
        emailInvalid: (
            <FormattedMessage
                id="dbl.authmodal.auth.errors.emailInvalid"
                defaultMessage="Please enter a valid email address."
            />
        ),
        emailRequired: (
            <FormattedMessage
                id="dbl.authmodal.auth.errors.emailRequired"
                defaultMessage="Email address is required."
            />
        ),
        invalidLink: (
            <FormattedMessage
                id="dbl.authmodal.auth.errors.invalidLink"
                defaultMessage="Invalid Link"
            />
        ),
        invalidLogin: (
            <FormattedMessage
                id="dbl.authmodal.auth.errors.invalidLogin"
                defaultMessage="The email/password combination does not match our records. Passwords are case sensitive."
            />
        ),
        invalidResetToken: (
            <FormattedMessage
                id="dbl.authmodal.auth.errors.invalidResetToken"
                defaultMessage="The link to reset your password is invalid. Please make sure that:<ul><li>The link was requested within the last 24 hours</li><li>The link has not been used before to reset your password</li><li>The link is spelled correctly</li><li>If a reset was requested multiple times, this is the most recently sent link</li></ul>"
            />
        ),
        nonSupportedTradeLogin: (
            <FormattedMessage
                id="dbl.authmodal.auth.errors.nonSupportedTradelogin"
                defaultMessage="The 1stdibs trade experience is not yet supported on this domain. Please log in at {usHomepage} or {gbHomepage}."
                values={{
                    usHomepage: (
                        <a href="https://www.1stdibs.com/">
                            <FormattedMessage
                                id="dbl.authmodal.auth.errors.usHomepage"
                                defaultMessage="1stdibs.com"
                            />
                        </a>
                    ),
                    gbHomepage: (
                        <a href="https://www.1stdibs.co.uk/">
                            <FormattedMessage
                                id="dbl.authmodal.auth.errors.gbHomepage"
                                defaultMessage="1stdibs.co.uk"
                            />
                        </a>
                    ),
                }}
            />
        ),
        passwordMatch: (
            <FormattedMessage
                id="dbl.authmodal.auth.errors.passwordMatch"
                defaultMessage="The passwords do not match. Please check the passwords that you entered."
            />
        ),
        passwordRequired: (
            <FormattedMessage
                id="dbl.authmodal.auth.errors.passwordRequired"
                defaultMessage="Password is required."
            />
        ),
        passwordShort: (
            <FormattedMessage
                id="dbl.authmodal.auth.errors.passwordShort"
                defaultMessage="Please enter a minimum of 5 characters."
            />
        ),
        support: (
            <FormattedMessage
                id="dbl.authmodal.auth.errors.support"
                defaultMessage="Authentication error! Please contact {mailLink}"
                values={{
                    mailLink: <a href="mailto:support@1stdibs.com">support@1stdibs.com</a>,
                }}
            />
        ),
        socialLoginOnly: (
            <FormattedMessage
                id="dbl.authmodal.auth.errors.socialLoginOnly"
                defaultMessage="You've registered through Facebook. Continue with Facebook below."
            />
        ),
        socialLoginNoEmailReturned: (
            <FormattedMessage
                id="dbl.authmodal.auth.errors.socialLoginNoEmailReturned"
                defaultMessage="Your Facebook account needs a valid email to continue. Please try again with an alternative Facebook login."
            />
        ),
        emailOnlyCreatePassword: (
            <FormattedMessage
                id="dbl.authmodal.auth.errors.emailOnlyCreatePassword"
                defaultMessage="Please click on the link below to create a password for this account."
            />
        ),
    },
    benefitsModal: {
        registrationHeader: (
            <FormattedMessage
                id="dbl.authmodal.auth.benefitsModal.registrationHeader"
                defaultMessage="Sign Up to Get Exclusive Benefits"
            />
        ),
        recommendations: (
            <FormattedMessage
                id="dbl.authmodal.auth.benefitsModal.recommendations"
                defaultMessage="Personalized item recommendations"
            />
        ),
        saleAlerts: (
            <FormattedMessage
                id="dbl.authmodal.auth.benefitsModal.saleAlerts"
                defaultMessage="ITEM SALE ALERTS"
            />
        ),
        directCommunication: (
            <FormattedMessage
                id="dbl.authmodal.auth.benefitsModal.directCommunication"
                defaultMessage="Direct communication with sellers"
            />
        ),
    },
    designerConnectionModal: {
        header: (
            <FormattedMessage
                id="dbl.authmodal.auth.designerConnectionModal.header"
                defaultMessage="Log In to Send Project Information"
            />
        ),
        subText: (
            <FormattedMessage
                id="dbl.authmodal.auth.designerConnectionModal.subText"
                defaultMessage="We'll start matching you with award-winning designers"
            />
        ),
    },
    emailOnlySaveItemFlow: {
        registrationCompletionHeader: (
            <FormattedMessage
                id="dbl.authmodal.auth.emailOnlySaveItemFlow.registrationCompletionHeader"
                defaultMessage="Your Item Has Been Saved"
            />
        ),
    },
    emailOnlySaveItemToFolderFlow: {
        registrationCompletionHeader: (
            <FormattedMessage
                id="dbl.authmodal.auth.emailOnlySaveItemToFolderFlow.registrationCompletionHeader"
                defaultMessage="Your Item Has Been Saved to a Folder"
            />
        ),
    },
    emailOnlyFollowDealerFlow: {
        registrationCompletionHeader: (
            <FormattedMessage
                id="dbl.authmodal.auth.emailOnlyFollowDealerFlow.registrationCompletionHeader"
                defaultMessage="Complete Signup"
            />
        ),
        registrationCompletion: (
            <FormattedMessage
                id="dbl.authmodal.auth.emailOnlyFollowDealerFlow.registrationCompletion"
                defaultMessage="Your account has been created. Complete signup by creating a password to gain full access to 1stdibs and get personalized email updates on what you follow."
            />
        ),
    },
    emailOnlyContactDealerFlow: {
        header: (
            <FormattedMessage
                id="dbl.authmodal.auth.emailOnlyContactDealerFlow.header"
                defaultMessage="Sign Up to Send Your Message"
            />
        ),
        registrationCompletion: (
            <FormattedMessage
                id="dbl.authmodal.auth.emailOnlyContactDealerFlow.registrationCompletion"
                defaultMessage="Create a password to complete signup. You’ll be able to view all your conversations with sellers and gain full access to 1stdibs."
            />
        ),
        registrationCompletionHeader: (
            <FormattedMessage
                id="dbl.authmodal.auth.emailOnlyContactDealerFlow.registrationCompletionHeader"
                defaultMessage="Your Message Has Been Sent"
            />
        ),
        fullAccountRegistrationCompletion: (
            <FormattedMessage
                id="dbl.authmodal.auth.emailOnlyContactDealerFlow.fullAccountRegistrationCompletion"
                defaultMessage="Your account has been created. Complete signup below to send your message."
            />
        ),
        fullAccountRegistrationCompletionHeader: (
            <FormattedMessage
                id="dbl.authmodal.auth.emailOnlyContactDealerFlow.fullAccountRegistrationCompletionHeader"
                defaultMessage="Complete Signup to Send Your Message"
            />
        ),
    },
    emailOnlyRegistrationFlow: {
        registrationCompletion: (
            <FormattedMessage
                id="dbl.authmodal.auth.emailOnlyRegistrationFlow.subtext"
                defaultMessage="Your account has been created. Complete signup by creating a password to gain full access to 1stdibs and get personalized email updates."
            />
        ),
        registrationDescription: (
            <FormattedMessage
                id="dbl.authmodal.auth.emailOnlyRegistrationFlow.registrationDescription"
                defaultMessage="Create an account to access the best selection of rare and beautiful objects from around the world."
            />
        ),
        continue: (
            <FormattedMessage
                id="dbl.authmodal.auth.emailOnlyRegistrationFlow.continue"
                defaultMessage="Continue"
            />
        ),
        completeSignup: (
            <FormattedMessage
                id="dbl.authmodal.auth.emailOnlyRegistrationFlow.completeSignup"
                defaultMessage="Complete Signup"
            />
        ),
    },
};

Success.propTypes = {
    email: PropTypes.string,
};
